import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ApolloQueryResult } from '@apollo/client';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import SwitchFormField from '@components/form-fields/SwitchFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import WithLinkFormField from '@components/form-fields/WithLinkFormField';
import TabPanel from '@components/layout/tabs/TabPanel';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import ProposalPolicyPersonDrawer from '@features/production/proposal/drawers/proposal-policy-person-drawer/ProposalPolicyPersonDrawer';
import { useHandlePersonDrawerSuccess } from '@features/production/proposal/hooks';
import { useGetPersonAddressList } from '@features/production/proposal/proposal.slice';
import {
  GetBusinessPartnerPersonsResponse,
  GetBusinessPartnerPersonsVariables,
  IBusinessPartnerPerson
} from '@features/production/proposal/types';
import PersonAddressDrawer from '@modules/salesforce/persons/person-details-page/person-address-drawer/PersonAddressDrawer';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Tab, Tabs } from '@mui/material';
import { MAIN_THEME } from '@styles';
import { isEmpty } from '@utils/validationUtils';

import { FormValues as AmendmentFormValues } from '../types';

import { FieldArray, useFormikContext } from 'formik';
import { makeStyles } from 'tss-react/mui';

export interface IMedicalInsuredPerson {
  insuredID: string;
  isPrincipal: boolean;
  relation: string;
  addressID: string;
  addressDetails: string;
  cardNumber: string;
  dateOfBirth: string;
  passportNumber: string;
  continuity: string;
  tpaReference1: string;
  tpaReference2: string;
  grApplicable: string;
  exclusions: string;
  remarks: string;
  isNew: boolean;
  isRemoved: boolean;
  sequence: number;
  personType: string;
}

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: 4,
    padding: '20px 12px 16px 18px',
    marginBottom: 14,
    position: 'relative',
    borderTopLeftRadius: 'unset'
  },
  fieldRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '10px'
  },
  halfRowWidth: { width: '48%' },
  thirdRowWidth: { width: '31%' },
  quarterRowWidth: { width: '23%' },
  fullRowWidth: { width: '100%' },
  tabsContainer: {
    display: 'flex',
    alignItems: 'center',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    padding: 0,
    '&::-webkit-scrollbar': { height: '8px' },
    '&::-webkit-scrollbar-thumb': {
      background: MAIN_THEME.color.white86,
      borderRadius: 4
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: MAIN_THEME.color.white65
    }
  },
  tabsWrapper: { display: 'flex', alignItems: 'flex-end' },
  addBtn: {
    marginLeft: '2px',
    minWidth: 'auto',
    minHeight: 'auto',
    backgroundColor: 'transparent',
    padding: 0,
    margin: '0 0 0 10px',
    alignSelf: 'center',
    border: 'none',
    cursor: 'pointer'
  },
  tabsRoot: {
    minHeight: 'auto',
    overflowX: 'auto',
    '& .MuiTabs-flexContainer': { borderBottom: 'none' }
  },
  indicator: { display: 'none' },
  tabRoot: {
    textTransform: 'none',
    minWidth: 72,
    minHeight: '31px',
    padding: '4px 8px',
    marginRight: 4,
    color: MAIN_THEME.color.black47,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderBottom: 'none',
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    backgroundColor: MAIN_THEME.color.white92,
    fontFamily: MAIN_THEME.font.regular,
    '&.Mui-selected': {
      color: MAIN_THEME.color.black,
      fontFamily: MAIN_THEME.font.medium,
      backgroundColor: MAIN_THEME.color.white98,
      borderBottom: 'none',
      fontWeight: 600
    }
  },
  tabLabel: {
    fontSize: MAIN_THEME.fontSize.sm,
    fontFamily: MAIN_THEME.font.regular,
    lineHeight: '18px',
    color: MAIN_THEME.color.black,
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    backgroundColor: 'transparent'
  },
  tabIconButton: { marginLeft: 4, padding: 0 },
  closeIcon: { fontSize: MAIN_THEME.fontSize.rg }
}));

type Props = {
  status: string;
  lineCode: string;
  primaryBPId: string;
  maxNumber: number;
  personsOptions: Record<string, string>;
  setPersonsOptions: React.Dispatch<
    React.SetStateAction<Record<string, string>>
  >;
  refetchPersons: (
    variables?: Partial<GetBusinessPartnerPersonsVariables>
  ) => Promise<ApolloQueryResult<GetBusinessPartnerPersonsResponse>>;
  continuityList: Record<string, string>;
  relationsList: Record<string, string>;
};

const MedicalInsuredsDetailsWidgetAtom: React.FC<Props> = ({
  status,
  lineCode,
  primaryBPId,
  maxNumber,
  personsOptions,
  setPersonsOptions,
  refetchPersons,
  continuityList,
  relationsList
}) => {
  const { classes } = useStyles();
  const { id: amendmentId } = useParams<{ id: string }>();

  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<AmendmentFormValues>();

  const s = String(status || '').toUpperCase();
  const isDisabled = s === 'CONFIRMED' || s === 'CANCELLED';
  const isMedicalLine = lineCode === '19';
  const isTravelLine = lineCode === '48';
  const isGroupMedicalCertificate = lineCode === '6';
  const showMedicalInsuredDetailFields =
    isMedicalLine || isGroupMedicalCertificate;
  const isIndividualPerson = (personType?: string) =>
    String(personType ?? '').toUpperCase() === 'INDIVIDUAL';

  const [selectedTab, setSelectedTab] = React.useState(0);
  const [newPersonDrawerOpen, setNewPersonDrawerOpen] = useState(false);
  const [newAddressDrawerOpen, setNewAddressDrawerOpen] = useState(false);
  const [lists, setLists] = useState<{
    insuredAddresses: Record<string | number, string>;
  }>({
    insuredAddresses: {}
  });

  const handleTabChange = (_e: React.SyntheticEvent, v: number) =>
    setSelectedTab(v);

  const getAdherentSequence = (insureds: any[], idx: number) => {
    let seq = 0;

    for (let j = 0; j <= idx; j++) {
      if (!insureds[j].isPrincipal) seq++;
    }

    return seq;
  };

  const medicalInsureds = values.medicalInsureds || [];

  const addressesQuery = useGetPersonAddressList({
    variables: { selectedPersonID: '' },
    skip: true
  });

  const fetchAddresses = async (personId: string) => {
    const result = await addressesQuery.refetch({ selectedPersonID: personId });
    const items = result?.data?.salesforce?.getPersonAddressList?.items || [];
    const map: Record<string | number, string> = {};
    let primary: string | number = '';

    for (const addr of items) {
      if (addr?.id) {
        const { building, street, city } = addr?.address || {};
        const country = addr?.country?.title;
        const zone = addr?.zone?.title;

        const text = [building, street, zone, city, country]
          .filter(Boolean)
          .join(', ');
        map[addr.id] = text;
        if (addr.isPrimary) {
          primary = addr.id;
        }
      }
    }

    return { map, primary };
  };

  useEffect(() => {
    const loadAddresses = async () => {
      if (!values.medicalInsureds || values.medicalInsureds.length === 0) {
        return;
      }

      const addressMap: Record<string | number, string> = {};

      for (const insured of values.medicalInsureds) {
        if (insured.insuredID && insured.addressID && insured.addressDetails) {
          addressMap[insured.addressID] = insured.addressDetails;
        }
      }

      if (Object.keys(addressMap).length > 0) {
        setLists((prev) => ({
          ...prev,
          insuredAddresses: { ...prev.insuredAddresses, ...addressMap }
        }));
      }
    };

    loadAddresses();
  }, [values.medicalInsureds]);

  const { handlePersonDrawerSubmit } = useHandlePersonDrawerSuccess(
    setPersonsOptions,
    setFieldValue as any,
    refetchPersons
  );

  const handleDrawerSubmitSuccess = async (person: IBusinessPartnerPerson) => {
    const personId = person.id;
    const name = `${person.code} - ${person.fullName}`;
    const type = person.type;
    const dob = person.dateOfBirth;

    const { map, primary } = await fetchAddresses(String(person.id));
    setLists((prev) => ({
      ...prev,
      insuredAddresses: { ...prev.insuredAddresses, ...map }
    }));

    await handlePersonDrawerSubmit({
      personId,
      name,
      fieldUpdates: [
        { path: `medicalInsureds[${selectedTab}].insuredID`, value: person.id },
        { path: `medicalInsureds[${selectedTab}].addressID`, value: primary },
        {
          path: `medicalInsureds[${selectedTab}].dateOfBirth`,
          value: type === 'INDIVIDUAL' ? dob || '' : ''
        },
        {
          path: `medicalInsureds[${selectedTab}].passportNumber`,
          value: person.passportNumber || ''
        },
        {
          path: `medicalInsureds[${selectedTab}].personType`,
          value: type || ''
        }
      ]
    });
    setNewPersonDrawerOpen(false);
  };

  const handleAddressDrawerSubmit = async (
    addrId: string,
    addrText: string,
    personId: string
  ) => {
    setLists((prev) => ({
      ...prev,
      insuredAddresses: { ...prev.insuredAddresses, [addrId]: addrText }
    }));

    await setFieldValue(
      `medicalInsureds[${selectedTab}].addressID`,
      addrId || '',
      false
    );
    setNewAddressDrawerOpen(false);
  };

  const onPersonChange = async (v: string, idx: number) => {
    const base = `medicalInsureds[${idx}]`;

    await setFieldValue(`${base}.insuredID`, v, false);

    if (!v || isEmpty(v)) {
      await setFieldValue(`${base}.addressID`, '', false);

      return;
    }

    const { map, primary } = await fetchAddresses(v);

    setLists((prev) => ({
      ...prev,
      insuredAddresses: { ...prev.insuredAddresses, ...map }
    }));

    await setFieldValue(`${base}.addressID`, primary || '', false);

    try {
      const response = await refetchPersons();
      const persons =
        response?.data?.salesforce?.getBusinessPartnerPersons ?? [];
      const person = persons.find((p: IBusinessPartnerPerson) => p.id === v);

      if (person) {
        await setFieldValue(
          `${base}.dateOfBirth`,
          person.dateOfBirth || '',
          false
        );
        await setFieldValue(`${base}.personType`, person.type || '', false);
        if (lineCode === '48') {
          await setFieldValue(
            `${base}.passportNumber`,
            person.passportNumber || '',
            false
          );
        }
      }
    } catch (error) {
      void error;
    }
  };

  const onAdd = (push: (value: IMedicalInsuredPerson) => void) => {
    const adherent: IMedicalInsuredPerson = {
      insuredID: '',
      isPrincipal: false,
      relation: '',
      addressID: '',
      addressDetails: '',
      cardNumber: '',
      dateOfBirth: '',
      passportNumber: '',
      continuity: '',
      tpaReference1: '',
      tpaReference2: '',
      grApplicable: '',
      exclusions: '',
      remarks: '',
      isNew: true,
      isRemoved: false,
      sequence: 0,
      personType: 'INDIVIDUAL'
    };
    push(adherent);
    setSelectedTab(medicalInsureds.length);
  };

  const onDelete = (remove: (index: number) => void, index: number) => {
    remove(index);
    const nextLength = (medicalInsureds?.length ?? 1) - 1;
    if (selectedTab >= nextLength) setSelectedTab(Math.max(0, nextLength - 1));

  };

  const filteredRelations = Object.fromEntries(
    Object.entries(relationsList).filter(
      ([key]) => key.toUpperCase() !== 'PRINCIPAL'
    )
  );

  const activeInsuredsCount = medicalInsureds.filter(
    (ins: any) => !ins.isPrincipal && !ins.isRemoved
  ).length;

  const canAddMore = activeInsuredsCount < maxNumber - 1;

  return (
    <WidgetPaper>
      <WidgetSection title="Insureds Details">
        <FieldArray
          name="medicalInsureds"
          render={({ push, remove }) => (
            <>
              <div className={classes.tabsContainer}>
                <div className={classes.tabsWrapper}>
                  {newPersonDrawerOpen && (
                    <ProposalPolicyPersonDrawer
                      open
                      onClose={() => setNewPersonDrawerOpen(false)}
                      section="insured"
                      mainBusinessPartnerId={primaryBPId ?? ''}
                      onSubmitSuccess={handleDrawerSubmitSuccess}
                      lineExternalCode={lineCode}
                      proposalId={amendmentId}
                    />
                  )}
                  {newAddressDrawerOpen && (
                    <PersonAddressDrawer
                      open
                      isProposal
                      onClose={() => setNewAddressDrawerOpen(false)}
                      personId={medicalInsureds[selectedTab]?.insuredID}
                      onSubmitSuccess={async (
                        addrId: string,
                        addrText: string,
                        personId: string
                      ) => {
                        await handleAddressDrawerSubmit(
                          addrId,
                          addrText,
                          personId
                        );
                      }}
                      onSuccess={() => undefined}
                    />
                  )}
                  <Tabs
                    value={
                      medicalInsureds.length
                        ? Math.min(selectedTab, medicalInsureds.length - 1)
                        : 0
                    }
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    classes={{
                      root: classes.tabsRoot,
                      indicator: classes.indicator
                    }}
                  >
                    {medicalInsureds.map((insured: any, i: number) => (
                      <Tab
                        key={`insured-tab-${i}`}
                        className={classes.tabRoot}
                        label={
                          <div className={classes.tabLabel}>
                            {insured.isPrincipal
                              ? 'Principal'
                              : `Adherent ${getAdherentSequence(medicalInsureds, i)}`}
                            {!insured.isPrincipal && insured.isNew && (
                              <IconButton
                                type="button"
                                size="small"
                                disabled={isDisabled}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(remove, i);
                                }}
                                aria-label="Remove Insured"
                                className={classes.tabIconButton}
                              >
                                <CloseIcon className={classes.closeIcon} />
                              </IconButton>
                            )}
                          </div>
                        }
                      />
                    ))}
                  </Tabs>
                  {canAddMore && (
                    <IconButton
                      type="button"
                      className={classes.addBtn}
                      disabled={isDisabled}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onAdd(push);
                      }}
                      aria-label="Add"
                    >
                      <AddIcon color="primary" />
                    </IconButton>
                  )}
                </div>
              </div>

              {medicalInsureds.map((insured: any, i: number) =>
                selectedTab === i ? (
                  <TabPanel key={`insured-panel-${i}`} index={i}>
                    <div className={classes.container}>
                      {/* Row 1: For Principal: Insured + Address | For Adherent: Insured + Relation + Card Number */}
                      {insured.isPrincipal ? (
                        <div className={classes.fieldRow}>
                          <div className={classes.halfRowWidth}>
                            <WithLinkFormField
                              linkText=""
                              addSpace={false}
                              onLinkClick={() => {}}
                            >
                              <SelectFormField
                                key={`medicalInsureds[${i}].insuredID`}
                                name={`medicalInsureds[${i}].insuredID`}
                                title="Insured*"
                                placeholder="Insured"
                                value={insured.insuredID}
                                disabled
                                error={
                                  touched?.medicalInsureds?.[i]?.insuredID
                                    ? (errors as any)?.medicalInsureds?.[i]
                                        ?.insuredID
                                    : ''
                                }
                                onBlur={(_, e) => handleBlur(e)}
                                onChange={(v) => {
                                  onPersonChange(v, i);
                                }}
                                selectOptions={personsOptions}
                                material
                              />
                            </WithLinkFormField>
                          </div>
                          <div className={classes.halfRowWidth}>
                            <WithLinkFormField
                              linkText=""
                              addSpace={false}
                              onLinkClick={() => {}}
                            >
                              <SelectFormField
                                key={`medicalInsureds[${i}].addressID`}
                                name={`medicalInsureds[${i}].addressID`}
                                title="Insured Address*"
                                placeholder="Insured Address"
                                value={insured.addressID}
                                error={
                                  touched?.medicalInsureds?.[i]?.addressID
                                    ? (errors as any)?.medicalInsureds?.[i]
                                        ?.addressID
                                    : ''
                                }
                                onBlur={(_, e) => handleBlur(e)}
                                onChange={(v) =>
                                  setFieldValue(
                                    `medicalInsureds[${i}].addressID`,
                                    v
                                  )
                                }
                                disabled
                                selectOptions={lists.insuredAddresses}
                                material
                              />
                            </WithLinkFormField>
                          </div>
                        </div>
                      ) : (
                        <div className={classes.fieldRow}>
                          <div
                            className={
                              isTravelLine
                                ? classes.halfRowWidth
                                : classes.thirdRowWidth
                            }
                          >
                            <WithLinkFormField
                              linkText={
                                insured.isNew && !isDisabled ? 'Add Person' : ''
                              }
                              addSpace={insured.isNew && !isDisabled}
                              onLinkClick={(e?: any) => {
                                e?.preventDefault?.();
                                setNewPersonDrawerOpen(true);
                              }}
                            >
                              <SelectFormField
                                key={`medicalInsureds[${i}].insuredID`}
                                name={`medicalInsureds[${i}].insuredID`}
                                title="Insured*"
                                placeholder="Insured"
                                value={insured.insuredID}
                                disabled={isDisabled || !insured.isNew}
                                error={
                                  touched?.medicalInsureds?.[i]?.insuredID
                                    ? (errors as any)?.medicalInsureds?.[i]
                                        ?.insuredID
                                    : ''
                                }
                                onBlur={(_, e) => handleBlur(e)}
                                onChange={(v) => {
                                  onPersonChange(v, i);
                                }}
                                selectOptions={personsOptions}
                                material
                              />
                            </WithLinkFormField>
                          </div>
                          <div
                            className={
                              isTravelLine
                                ? classes.halfRowWidth
                                : classes.thirdRowWidth
                            }
                          >
                            <SelectFormField
                              key={`medicalInsureds[${i}].relation`}
                              name={`medicalInsureds[${i}].relation`}
                              title="Relation*"
                              placeholder="Relation"
                              value={insured.relation}
                              disabled={isDisabled}
                              error={
                                touched?.medicalInsureds?.[i]?.relation
                                  ? (errors as any)?.medicalInsureds?.[i]
                                      ?.relation
                                  : ''
                              }
                              onBlur={(_, e) => handleBlur(e)}
                              onChange={(v) =>
                                setFieldValue(
                                  `medicalInsureds[${i}].relation`,
                                  v
                                )
                              }
                              selectOptions={filteredRelations}
                              material
                            />
                          </div>
                          {showMedicalInsuredDetailFields && (
                            <div className={classes.thirdRowWidth}>
                              <TextInputFormField
                                key={`medicalInsureds[${i}].cardNumber`}
                                name={`medicalInsureds[${i}].cardNumber`}
                                title="Card Number"
                                placeholder="Card Number"
                                value={insured.cardNumber}
                                disabled={isDisabled}
                                error={
                                  touched?.medicalInsureds?.[i]?.cardNumber
                                    ? (errors as any)?.medicalInsureds?.[i]
                                        ?.cardNumber
                                    : ''
                                }
                                onBlur={handleBlur}
                                onChange={(e) =>
                                  setFieldValue(
                                    `medicalInsureds[${i}].cardNumber`,
                                    e.target.value
                                  )
                                }
                                material
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* Row 2: For Principal (line 19): Card Number, Date of Birth, Continuity, TPA Reference 1 (4 fields) */}
                      {/* Row 2: For Adherent (line 19): Date of Birth, Continuity, TPA Reference 1, TPA Reference 2 (4 fields) */}
                      {showMedicalInsuredDetailFields && (
                        <div className={classes.fieldRow}>
                          {insured.isPrincipal && (
                            <div className={classes.quarterRowWidth}>
                              <TextInputFormField
                                key={`medicalInsureds[${i}].cardNumber`}
                                name={`medicalInsureds[${i}].cardNumber`}
                                title="Card Number"
                                placeholder="Card Number"
                                value={insured.cardNumber}
                                disabled={isDisabled}
                                error={
                                  touched?.medicalInsureds?.[i]?.cardNumber
                                    ? (errors as any)?.medicalInsureds?.[i]
                                        ?.cardNumber
                                    : ''
                                }
                                onBlur={handleBlur}
                                onChange={(e) =>
                                  setFieldValue(
                                    `medicalInsureds[${i}].cardNumber`,
                                    e.target.value
                                  )
                                }
                                material
                              />
                            </div>
                          )}
                          {isIndividualPerson(insured.personType) && (
                            <div className={classes.quarterRowWidth}>
                              <DatePickerFormField
                                key={`medicalInsureds[${i}].dateOfBirth`}
                                name={`medicalInsureds[${i}].dateOfBirth`}
                                title="Date of Birth*"
                                placeholder="Date of Birth"
                                value={insured.dateOfBirth}
                                disabled={isDisabled}
                                error={
                                  touched?.medicalInsureds?.[i]?.dateOfBirth
                                    ? (errors as any)?.medicalInsureds?.[i]
                                        ?.dateOfBirth
                                    : ''
                                }
                                onBlur={handleBlur}
                                onDateChange={(v) => {
                                  setFieldValue(
                                    `medicalInsureds[${i}].dateOfBirth`,
                                    v
                                  );
                                }}
                                material
                              />
                            </div>
                          )}
                          <div className={classes.quarterRowWidth}>
                            <SelectFormField
                              key={`medicalInsureds[${i}].continuity`}
                              name={`medicalInsureds[${i}].continuity`}
                              title="Continuity"
                              placeholder="Continuity"
                              value={insured.continuity}
                              disabled={isDisabled}
                              error={
                                touched?.medicalInsureds?.[i]?.continuity
                                  ? (errors as any)?.medicalInsureds?.[i]
                                      ?.continuity
                                  : ''
                              }
                              onBlur={(_, e) => handleBlur(e)}
                              onChange={(v) =>
                                setFieldValue(
                                  `medicalInsureds[${i}].continuity`,
                                  v
                                )
                              }
                              selectOptions={continuityList}
                              material
                            />
                          </div>
                          <div className={classes.quarterRowWidth}>
                            <TextInputFormField
                              key={`medicalInsureds[${i}].tpaReference1`}
                              name={`medicalInsureds[${i}].tpaReference1`}
                              title="TPA Reference 1"
                              placeholder="TPA Reference 1"
                              value={insured.tpaReference1}
                              disabled={isDisabled}
                              error={
                                touched?.medicalInsureds?.[i]?.tpaReference1
                                  ? (errors as any)?.medicalInsureds?.[i]
                                      ?.tpaReference1
                                  : ''
                              }
                              onBlur={handleBlur}
                              onChange={(e) =>
                                setFieldValue(
                                  `medicalInsureds[${i}].tpaReference1`,
                                  e.target.value
                                )
                              }
                              material
                            />
                          </div>
                          {!insured.isPrincipal && (
                            <div className={classes.quarterRowWidth}>
                              <TextInputFormField
                                key={`medicalInsureds[${i}].tpaReference2`}
                                name={`medicalInsureds[${i}].tpaReference2`}
                                title="TPA Reference 2"
                                placeholder="TPA Reference 2"
                                value={insured.tpaReference2}
                                disabled={isDisabled}
                                error={
                                  touched?.medicalInsureds?.[i]?.tpaReference2
                                    ? (errors as any)?.medicalInsureds?.[i]
                                        ?.tpaReference2
                                    : ''
                                }
                                onBlur={handleBlur}
                                onChange={(e) =>
                                  setFieldValue(
                                    `medicalInsureds[${i}].tpaReference2`,
                                    e.target.value
                                  )
                                }
                                material
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {showMedicalInsuredDetailFields && insured.isPrincipal && (
                        <div className={classes.fieldRow}>
                          <div className={classes.quarterRowWidth}>
                            <TextInputFormField
                              key={`medicalInsureds[${i}].tpaReference2`}
                              name={`medicalInsureds[${i}].tpaReference2`}
                              title="TPA Reference 2"
                              placeholder="TPA Reference 2"
                              value={insured.tpaReference2}
                              disabled={isDisabled}
                              error={
                                touched?.medicalInsureds?.[i]?.tpaReference2
                                  ? (errors as any)?.medicalInsureds?.[i]
                                      ?.tpaReference2
                                  : ''
                              }
                              onBlur={handleBlur}
                              onChange={(e) =>
                                setFieldValue(
                                  `medicalInsureds[${i}].tpaReference2`,
                                  e.target.value
                                )
                              }
                              material
                            />
                          </div>
                        </div>
                      )}

                      {showMedicalInsuredDetailFields && !insured.isPrincipal && (
                        <div className={classes.fieldRow}>
                          <div className={classes.quarterRowWidth}>
                            <SelectFormField
                              key={`medicalInsureds[${i}].grApplicable`}
                              name={`medicalInsureds[${i}].grApplicable`}
                              title="GR Applicable"
                              placeholder="GR Applicable"
                              value={insured.grApplicable}
                              disabled={isDisabled}
                              error={
                                touched?.medicalInsureds?.[i]?.grApplicable
                                  ? (errors as any)?.medicalInsureds?.[i]
                                      ?.grApplicable
                                  : ''
                              }
                              onBlur={(_, e) => handleBlur(e)}
                              onChange={(v) =>
                                setFieldValue(
                                  `medicalInsureds[${i}].grApplicable`,
                                  v
                                )
                              }
                              selectOptions={{ YES: 'Yes', NO: 'No' }}
                              material
                            />
                          </div>
                        </div>
                      )}

                      {/* Travel (line 48): Card Number + DOB on row 2, Passport on row 3 */}
                      {isTravelLine && (
                        <>
                          <div className={classes.fieldRow}>
                            <div className={classes.halfRowWidth}>
                              <TextInputFormField
                                key={`medicalInsureds[${i}].cardNumber`}
                                name={`medicalInsureds[${i}].cardNumber`}
                                title="Card Number"
                                placeholder="Card Number"
                                value={insured.cardNumber}
                                disabled={isDisabled}
                                error={
                                  touched?.medicalInsureds?.[i]?.cardNumber
                                    ? (errors as any)?.medicalInsureds?.[i]
                                        ?.cardNumber
                                    : ''
                                }
                                onBlur={handleBlur}
                                onChange={(e) =>
                                  setFieldValue(
                                    `medicalInsureds[${i}].cardNumber`,
                                    e.target.value
                                  )
                                }
                                material
                              />
                            </div>
                            {isIndividualPerson(insured.personType) && (
                              <div className={classes.halfRowWidth}>
                                <DatePickerFormField
                                  key={`medicalInsureds[${i}].dateOfBirth`}
                                  name={`medicalInsureds[${i}].dateOfBirth`}
                                  title="Date of Birth*"
                                  placeholder="Date of Birth"
                                  value={insured.dateOfBirth}
                                  disabled={isDisabled}
                                  error={
                                    touched?.medicalInsureds?.[i]?.dateOfBirth
                                      ? (errors as any)?.medicalInsureds?.[i]
                                          ?.dateOfBirth
                                      : ''
                                  }
                                  onBlur={handleBlur}
                                  onDateChange={(v) => {
                                    setFieldValue(
                                      `medicalInsureds[${i}].dateOfBirth`,
                                      v
                                    );
                                  }}
                                  material
                                />
                              </div>
                            )}
                          </div>
                          <div className={classes.fieldRow}>
                            <div className={classes.halfRowWidth}>
                              <TextInputFormField
                                key={`medicalInsureds[${i}].passportNumber`}
                                name={`medicalInsureds[${i}].passportNumber`}
                                title="Passport Number*"
                                placeholder="Passport Number"
                                value={insured.passportNumber}
                                disabled={isDisabled}
                                error={
                                  touched?.medicalInsureds?.[i]?.passportNumber
                                    ? (errors as any)?.medicalInsureds?.[i]
                                        ?.passportNumber
                                    : ''
                                }
                                onBlur={handleBlur}
                                onChange={(e) =>
                                  setFieldValue(
                                    `medicalInsureds[${i}].passportNumber`,
                                    e.target.value
                                  )
                                }
                                material
                              />
                            </div>
                          </div>
                        </>
                      )}

                      {/* Exclusions - full width */}
                      <div className={classes.fieldRow}>
                        <div className={classes.fullRowWidth}>
                          <TextInputFormField
                            key={`medicalInsureds[${i}].exclusions`}
                            name={`medicalInsureds[${i}].exclusions`}
                            title="Exclusions"
                            placeholder="Exclusions"
                            value={insured.exclusions}
                            disabled={isDisabled}
                            error={
                              touched?.medicalInsureds?.[i]?.exclusions
                                ? (errors as any)?.medicalInsureds?.[i]
                                    ?.exclusions
                                : ''
                            }
                            onBlur={handleBlur}
                            onChange={(e) =>
                              setFieldValue(
                                `medicalInsureds[${i}].exclusions`,
                                e.target.value
                              )
                            }
                            material
                          />
                        </div>
                      </div>

                      {/* Remarks - full width */}
                      <div className={classes.fieldRow}>
                        <div className={classes.fullRowWidth}>
                          <TextInputFormField
                            key={`medicalInsureds[${i}].remarks`}
                            name={`medicalInsureds[${i}].remarks`}
                            title="Remarks"
                            placeholder="Remarks"
                            value={insured.remarks}
                            disabled={isDisabled}
                            error={
                              touched?.medicalInsureds?.[i]?.remarks
                                ? (errors as any)?.medicalInsureds?.[i]?.remarks
                                : ''
                            }
                            onBlur={handleBlur}
                            onChange={(e) =>
                              setFieldValue(
                                `medicalInsureds[${i}].remarks`,
                                e.target.value
                              )
                            }
                            material
                          />
                        </div>
                      </div>

                      {!insured.isPrincipal && !insured.isNew && (
                        <div className={classes.fieldRow}>
                          <div className={classes.halfRowWidth}>
                            <SwitchFormField
                              key={`medicalInsureds[${i}].isRemoved`}
                              name={`medicalInsureds[${i}].isRemoved`}
                              title="Is Removed"
                              onChange={async (_, nextChecked) => {
                                await setFieldValue(
                                  `medicalInsureds[${i}].isRemoved`,
                                  nextChecked
                                );
                              }}
                              value={insured.isRemoved}
                              disabled={isDisabled}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </TabPanel>
                ) : null
              )}
            </>
          )}
        />
      </WidgetSection>
    </WidgetPaper>
  );
};

export default MedicalInsuredsDetailsWidgetAtom;
